import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDBA } from '@/contexts/DBAContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { PhotoGallery } from '@/components/PhotoGallery';
import { toast } from '@/components/ui/sonner';
import {
  ArrowLeft, Mail, Phone, MapPin, Tag, Calendar, FileText,
  Briefcase, TrendingUp, Clock, DollarSign, ArrowRight, Camera, Receipt, MessageSquare, CheckCircle, Share2, ExternalLink, Calculator, Plus, Send, Save
} from 'lucide-react';
import { formatPhone, mapsUrl } from '@/lib/formatters';
import { CustomerHeader } from '@/components/customers/CustomerHeader';
import { CustomerEditDialog, CustomerEstimateDialog, CustomerMergeDialog } from '@/components/customers/CustomerDialogs';
import { CustomerTabs } from '@/components/customers/CustomerTabs';
import { CustomerInfoPanel, CustomerKpis, CustomerServiceInsights } from '@/components/customers/CustomerSummaryPanels';
import { CustomerSmsConsentPanel } from '@/components/customers/CustomerSmsConsentPanel';
import { ManualSmsDialog } from '@/components/communications/ManualSmsDialog';

const stageColors = {
  new: 'bg-sky-100 text-sky-700',
  contacted: 'bg-violet-100 text-violet-700',
  quoted: 'bg-amber-100 text-amber-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-red-100 text-red-700',
};

const statusColors = {
  tentative: 'bg-purple-100 text-purple-700',
  scheduled: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-zinc-100 text-zinc-600',
};

const quoteStatusColors = {
  draft: 'bg-zinc-100 text-zinc-600',
  sent: 'bg-sky-100 text-sky-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-700',
};

const invoiceStatusColors = {
  draft: 'bg-zinc-100 text-zinc-600',
  sent: 'bg-sky-100 text-sky-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-zinc-100 text-zinc-600',
};

const timelineIcons = {
  lead: FileText,
  quote: DollarSign,
  job: Briefcase,
  photo: Camera,
  invoice: Receipt,
  estimate: Calculator,
  note: MessageSquare,
  contact: Phone,
  message: MessageSquare,
};

const money = (value = 0) => `$${Number(value || 0).toLocaleString()}`;
const photoUrl = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/photos/file/${fileId}`;
const canUseProofPacket = (job) => job.status === 'completed' && (job.proof_packet_token || job.closeout_photo_requirements?.met);

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { dbas, getDBAName, getDBAColor } = useDBA();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editDbaIds, setEditDbaIds] = useState([]);
  const [editError, setEditError] = useState('');
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [estimateActuals, setEstimateActuals] = useState({ actual_labor_hours: 0, actual_workers: 2, actual_round_trip_miles: 0, actual_dump_fee: 0, actual_rental_cost: 0, actual_misc_cost: 0, actual_final_charged_price: 0, actual_notes: '' });
  const [estimateActionError, setEstimateActionError] = useState('');
  const [showMerge, setShowMerge] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [selectedDuplicateId, setSelectedDuplicateId] = useState('');
  const [mergeStrategy, setMergeStrategy] = useState('keep_primary');
  const [showSmsDialog, setShowSmsDialog] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const { data: hist } = await api.get(`/customers/${customerId}/history`);
      setData(hist);
    } catch { return; } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const proofUrl = (token) => `${window.location.origin}/proof/${token}`;

  const createProofPacket = async (job, open = false) => {
    try {
      const { data: packet } = await api.post(`/jobs/${job.id}/proof-packet`);
      const url = proofUrl(packet.packet.token);
      if (open) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Proof packet link copied');
      }
      fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create proof packet');
    }
  };

  const openEditCustomer = () => {
    setEditDbaIds(data?.customer?.dba_ids || []);
    setEditError('');
    setShowEdit(true);
  };

  const openMergeDialog = async () => {
    setShowMerge(true);
    setMergeError('');
    setSelectedDuplicateId('');
    setMergeLoading(true);
    try {
      const { data: candidates } = await api.get(`/customers/${customerId}/merge-candidates`);
      setMergeCandidates(candidates);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMergeError(typeof detail === 'string' ? detail : 'Could not load duplicate candidates');
    } finally {
      setMergeLoading(false);
    }
  };

  const mergeCustomer = async () => {
    if (!selectedDuplicateId) return;
    setMergeError('');
    try {
      const { data: result } = await api.post(`/customers/${customerId}/merge`, { duplicate_customer_id: selectedDuplicateId, strategy: mergeStrategy });
      const updatedCount = Object.values(result.references_updated || {}).reduce((sum, value) => sum + Number(value || 0), 0);
      toast.success(`Customer merged · ${updatedCount} linked records moved`);
      setShowMerge(false);
      setSelectedDuplicateId('');
      await fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMergeError(typeof detail === 'string' ? detail : 'Could not merge customers');
    }
  };

  const toggleEditDba = (id) => {
    setEditDbaIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const saveCustomer = async (event) => {
    event.preventDefault();
    setEditError('');
    const form = new FormData(event.target);
    try {
      await api.put(`/customers/${customerId}`, {
        name: form.get('name'),
        first_name: form.get('first_name') || '',
        last_name: form.get('last_name') || '',
        email: form.get('email') || '',
        phone: form.get('phone') || '',
        address: form.get('address') || '',
        city: form.get('city') || '',
        state: form.get('state') || '',
        zip_code: form.get('zip_code') || '',
        source: form.get('source') || 'manual',
        tags: String(form.get('tags') || '').split(',').map(tag => tag.trim()).filter(Boolean),
        dba_ids: editDbaIds,
      });
      setShowEdit(false);
      toast.success('Customer updated');
      fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setEditError(typeof detail === 'string' ? detail : 'Could not update customer');
    }
  };

  const openRecord = async (type, record) => {
    if (!record?.id) return;
    if (type === 'lead') navigate('/leads', { state: { openLeadId: record.id } });
    if (type === 'quote') navigate('/quotes', { state: { openQuoteId: record.id } });
    if (type === 'job') navigate('/jobs', { state: { openJobId: record.id } });
    if (type === 'invoice') navigate('/invoices', { state: { openInvoiceId: record.id } });
    if (type === 'estimate') {
      const fullEstimate = (record.estimate_number || record.calculation) ? record : data?.estimates?.find((estimate) => estimate.id === record.id);
      if (!fullEstimate) {
        toast.error('Estimate is no longer available');
        return;
      }
      openEstimateDetail(fullEstimate);
    }
  };

  const customerPayload = () => ({
    id: customer.id,
    name: customer.name,
    first_name: customer.first_name || '',
    last_name: customer.last_name || '',
    email: customer.email || '',
    phone: customer.phone || '',
    address: customer.address || '',
    city: customer.city || '',
    state: customer.state || '',
    zip_code: customer.zip_code || '',
    dba_ids: customer.dba_ids || [],
  });

  const createCustomerRecord = (type) => {
    const payload = customerPayload();
    if (type === 'quote') navigate('/quotes', { state: { createForCustomer: payload } });
    if (type === 'job') navigate('/jobs', { state: { createForCustomer: payload } });
    if (type === 'invoice') navigate('/invoices', { state: { createForCustomer: payload } });
    if (type === 'estimator') {
      const params = new URLSearchParams({
        customer_id: payload.id,
        customer_name: payload.name || '',
        customer_email: payload.email || '',
        customer_phone: payload.phone || '',
        customer_address: payload.address || '',
        dba_id: payload.dba_ids?.[0] || '',
      });
      navigate(`/estimator?${params.toString()}`);
    }
  };

  const openEstimateDetail = (estimate) => {
    const linkedQuoteMissing = Boolean(estimate.quote_id && !data?.quotes?.some((quote) => quote.id === estimate.quote_id));
    setSelectedEstimate({ ...estimate, linked_quote_missing: linkedQuoteMissing });
    setEstimateActionError('');
    setEstimateActuals({
      actual_labor_hours: estimate.actuals?.actual_labor_hours || 0,
      actual_workers: estimate.actuals?.actual_workers || 2,
      actual_round_trip_miles: estimate.actuals?.actual_round_trip_miles || 0,
      actual_dump_fee: estimate.actuals?.actual_dump_fee || 0,
      actual_rental_cost: estimate.actuals?.actual_rental_cost || 0,
      actual_misc_cost: estimate.actuals?.actual_misc_cost || 0,
      actual_final_charged_price: estimate.actuals?.actual_final_charged_price || estimate.calculation?.final_price || 0,
      actual_notes: estimate.actuals?.notes || '',
    });
  };

  const createQuoteFromEstimate = async () => {
    if (!selectedEstimate?.id) return;
    setEstimateActionError('');
    try {
      const { data: quote } = await api.post(`/estimator/estimates/${selectedEstimate.id}/quote`);
      toast.success(`Quote created: ${quote.quote_number}`);
      setSelectedEstimate(null);
      await fetchHistory();
      navigate('/quotes', { state: { openQuoteId: quote.id } });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setEstimateActionError(typeof detail === 'string' ? detail : 'Could not create quote from estimate');
    }
  };

  const saveEstimateActuals = async () => {
    if (!selectedEstimate?.id) return;
    setEstimateActionError('');
    try {
      const { data: updated } = await api.post(`/estimator/estimates/${selectedEstimate.id}/actuals`, estimateActuals);
      toast.success('Estimate actuals saved');
      setSelectedEstimate(updated);
      await fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setEstimateActionError(typeof detail === 'string' ? detail : 'Could not save actuals');
    }
  };

  const updateEstimateActual = (field, value) => {
    setEstimateActuals(prev => ({ ...prev, [field]: field === 'actual_notes' ? value : parseFloat(value) || 0 }));
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-zinc-200 rounded" />
        <div className="h-48 bg-zinc-200 rounded-md" />
        <div className="h-64 bg-zinc-200 rounded-md" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">Customer not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/customers')}>Back to Customers</Button>
      </div>
    );
  }

  const { customer, leads, estimates = [], quotes = [], jobs, invoices = [], photos = [], messages = [], timeline = [], activity, cross_sell_opportunities } = data;
  const totalLeadValue = leads.reduce((sum, l) => sum + (l.estimated_value || 0), 0);
  const totalJobValue = jobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0);
  const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const openBalance = invoices.filter(inv => !['paid', 'cancelled'].includes(inv.status)).reduce((sum, inv) => sum + (inv.total || 0), 0);
  const completedJobs = jobs.filter(j => j.status === 'completed').length;

  return (
    <div className="space-y-6 animate-fade-in">
      <CustomerHeader customer={customer} navigate={navigate} createCustomerRecord={createCustomerRecord} openEditCustomer={openEditCustomer} openMergeDialog={openMergeDialog} openSmsDialog={() => setShowSmsDialog(true)} />

      {/* KPI row restored */}
      <CustomerKpis leads={leads} jobs={jobs} completedJobs={completedJobs} totalLeadValue={totalLeadValue} totalJobValue={totalJobValue} openBalance={openBalance} money={money} />

      {/* DBA Association + Cross-sell restored */}
      <CustomerServiceInsights customer={customer} crossSellOpportunities={cross_sell_opportunities} getDBAColor={getDBAColor} getDBAName={getDBAName} navigate={navigate} />

      <CustomerInfoPanel customer={customer} />

      <CustomerSmsConsentPanel customer={customer} onUpdated={fetchHistory} />

      <CustomerTabs
        data={{ customerId, customer, leads, estimates, quotes, jobs, invoices, photos, messages, timeline, activity }}
        helpers={{ timelineIcons, statusColors, stageColors, quoteStatusColors, invoiceStatusColors, money, photoUrl, canUseProofPacket, proofUrl, getDBAColor, getDBAName }}
        actions={{ openRecord, createProofPacket, openEditCustomer }}
      />

      <CustomerEditDialog showEdit={showEdit} setShowEdit={setShowEdit} editError={editError} saveCustomer={saveCustomer} customer={customer} dbas={dbas} editDbaIds={editDbaIds} toggleEditDba={toggleEditDba} />

      <CustomerMergeDialog open={showMerge} onOpenChange={setShowMerge} customer={customer} candidates={mergeCandidates} loading={mergeLoading} selectedDuplicateId={selectedDuplicateId} setSelectedDuplicateId={setSelectedDuplicateId} mergeStrategy={mergeStrategy} setMergeStrategy={setMergeStrategy} mergeError={mergeError} mergeCustomer={mergeCustomer} />

      <CustomerEstimateDialog selectedEstimate={selectedEstimate} setSelectedEstimate={setSelectedEstimate} estimateActionError={estimateActionError} money={money} estimateActuals={estimateActuals} updateEstimateActual={updateEstimateActual} saveEstimateActuals={saveEstimateActuals} navigate={navigate} createQuoteFromEstimate={createQuoteFromEstimate} />
      <ManualSmsDialog open={showSmsDialog} onOpenChange={setShowSmsDialog} customerId={customer.id} onSent={fetchHistory} />
    </div>
  );
}
